import type {
  CredentialFormDescriptor,
  CredentialFormField,
} from "@api/shared-runtime/credential-form-descriptors";
import { CREDENTIAL_FORM_DESCRIPTORS } from "@api/shared-runtime/credential-form-descriptors";
import type { CredentialType } from "@api/shared-runtime/enums";
import type { Credential } from "../../types";

/** Comma-separated in the input, string array in the payload. */
export const parseStringList = (value: string): string[] =>
  value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

export const formatStringList = (value: unknown): string =>
  Array.isArray(value) ? value.join(", ") : "";

/** Keyed `"<source>.<key>"` so a column and a detail of the same name cannot
 * collide (both `state-license.stateCode` and `work-history.details.state`
 * exist). */
export type CredentialFormValues = Record<string, string | boolean>;

const fieldKey = (field: CredentialFormField) => `${field.source}.${field.key}`;

/** Existing value for a field, from the credential's current version. */
function currentValue(field: CredentialFormField, credential: Credential) {
  const version = credential.version;
  if (!version) {
    return undefined;
  }
  if (field.source === "column") {
    return (version as unknown as Record<string, unknown>)[field.key];
  }
  const details = (version.details ?? {}) as Record<string, unknown>;
  return details[field.key];
}

/**
 * Seed the form from what is on file. Everything is held as a string (or a
 * boolean for checkboxes) while editing and converted once on submit — coercing
 * a half-typed number or date on every keystroke fights the user.
 */
export function initialFormValues(
  descriptor: CredentialFormDescriptor,
  credential: Credential,
): CredentialFormValues {
  const values: CredentialFormValues = {};
  for (const field of descriptor.fields) {
    const existing = currentValue(field, credential);
    if (field.kind === "boolean") {
      values[fieldKey(field)] = existing === true;
      continue;
    }
    if (field.kind === "string-list") {
      values[fieldKey(field)] = formatStringList(existing);
      continue;
    }
    values[fieldKey(field)] =
      existing === null || existing === undefined ? "" : String(existing);
  }
  return values;
}

/**
 * Turn form values into the write payload.
 *
 * Empty strings become `undefined` rather than `""`: the `details` schemas are
 * `.strict()` and typed, so an empty string in a date field is a validation
 * failure, while an absent optional key is simply absent. Unchecked booleans
 * are dropped for the same reason — `false` and "not stated" are different
 * claims, and only the user ticking a box asserts the first.
 *
 * **Keys the form cannot edit are copied forward from the current version.**
 * Versions are immutable and a save mints a new one, so a key left out of this
 * payload is not "unchanged" — it is deleted. Without this, an organization
 * correcting a DEA number would silently wipe the nested address, claims
 * history or USMLE steps the provider had entered.
 */
export function buildSubstance(
  descriptor: CredentialFormDescriptor,
  values: CredentialFormValues,
  currentDetails?: Record<string, unknown> | null,
) {
  const columns: Record<string, string | undefined> = {};
  const details: Record<string, unknown> = {};

  // Server-owned keys carry forward for the same reason deferred ones do: the
  // form does not render them, so leaving them out of the payload would delete
  // provenance the user never knew was there.
  for (const key of [
    ...descriptor.unsupportedDetailKeys,
    ...(descriptor.serverOwnedDetailKeys ?? []),
  ]) {
    const carried = currentDetails?.[key];
    if (carried !== undefined) {
      details[key] = carried;
    }
  }

  for (const field of descriptor.fields) {
    const raw = values[fieldKey(field)];

    if (field.kind === "boolean") {
      if (raw === true) {
        details[field.key] = true;
      }
      continue;
    }

    const text = typeof raw === "string" ? raw.trim() : "";
    if (text === "") {
      continue;
    }

    if (field.source === "column") {
      columns[field.key] = text;
      continue;
    }

    if (field.kind === "number") {
      const parsed = Number(text);
      // A non-numeric string would fail `.parse` server-side with a message
      // about types rather than about this field, so drop it here.
      if (Number.isFinite(parsed)) {
        details[field.key] = parsed;
      }
      continue;
    }
    if (field.kind === "string-list") {
      const list = parseStringList(text);
      if (list.length > 0) {
        details[field.key] = list;
      }
      continue;
    }
    details[field.key] = text;
  }

  return {
    number: columns.number,
    stateCode: columns.stateCode,
    issuedAt: columns.issuedAt,
    expiresAt: columns.expiresAt,
    status: columns.status,
    details,
  };
}

/** One parsed upload, as the form's own values.
 *
 * Lives here rather than with the review panel because the panel is no longer
 * its only caller: creating a credential from an upload seeds the form with
 * these directly, instead of asking someone to press a button to copy them in.
 */
export function candidateFormValues(candidate: {
  credentialType: string;
  parsed: {
    number?: string | null;
    stateCode?: string | null;
    issuedAt?: string | null;
    expiresAt?: string | null;
    status?: string | null;
    details?: Record<string, unknown> | null;
  };
}): CredentialFormValues {
  const descriptor =
    CREDENTIAL_FORM_DESCRIPTORS[candidate.credentialType as CredentialType];
  if (!descriptor) {
    return {};
  }

  const values: CredentialFormValues = {};
  for (const field of descriptor.fields) {
    const raw =
      field.source === "column"
        ? candidate.parsed[field.key as keyof typeof candidate.parsed]
        : (candidate.parsed.details ?? {})[field.key];
    if (raw === null || raw === undefined || raw === "") {
      continue;
    }
    values[`${field.source}.${field.key}`] =
      field.kind === "boolean" ? Boolean(raw) : String(raw);
  }
  return values;
}
